import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import ReactDOM from "react-dom/client";

import App from "./App";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import "./i18n";
import { FoodpilotSnackbar } from "./utils/useSnackbar";
import { LanguageProvider } from "./context/LanguageProvider";
import { getApiUrl, getSureApiUrl } from "src/api/axiosClient/utils.ts";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router";
import { getEnv } from "src/utils/getEnv";
import { DndContext } from "@dnd-kit/core";
import { queryClient } from "./_Globals/queryClient/queryClient";
import * as React from "react";
import { Box } from "@mui/material";
import { Loading } from "./components/utils/Loading";
import { isAppInPDFMode } from "src/utils/rendering";
import { registerDatadogRum } from "src/utils/monitoring";
import { registerPreloadErrorReload } from "src/utils/reloadOnPreloadError";
import { redactSentryPayload } from "src/utils/redactSentryAuthTokens";

registerPreloadErrorReload();

const envName = getEnv("VITE_APP_ENV_NAME");

// Initialize Sentry with minimal config for error tracking
Sentry.init({
    dsn: "https://4f07d96d1d1768cd4b7265be9b5438d0@o4507050960551936.ingest.us.sentry.io/4508091974156288",
    environment: envName,
    enabled: envName !== "local",
    integrations: [
        Sentry.httpClientIntegration({
            failedRequestStatusCodes: [400, 599],
        }),
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        // Defer replay integration to reduce initial bundle
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [getApiUrl(), getSureApiUrl()],
    beforeSend: redactSentryPayload,
    beforeSendTransaction: redactSentryPayload,
    beforeSendSpan: redactSentryPayload,
    beforeBreadcrumb: redactSentryPayload,
});

// Load heavy monitoring features after initial render
setTimeout(async () => {
    try {
        const { datadogRum } = await import("@datadog/browser-rum");
        const { datadogConfig } = await import("../config/datadog");

        // Add Sentry Replay integration after initial load
        Sentry.addIntegration(
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        );

        if (datadogConfig.env === "production") {
            datadogRum.init(datadogConfig);
            registerDatadogRum(datadogRum);
            datadogRum.startSessionReplayRecording();
        }
    } catch (error) {
        console.error("Failed to load monitoring integrations:", error);
    }
}, 2000);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
    <QueryClientProvider client={queryClient}>
        <LanguageProvider>
            <DndContext>
                <FoodpilotSnackbar>
                    <Suspense
                        fallback={
                            <Box
                                sx={{
                                    minHeight: "100vh",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Loading size={40} />
                            </Box>
                        }
                    >
                        <App />
                    </Suspense>
                </FoodpilotSnackbar>
            </DndContext>
        </LanguageProvider>
        {!isAppInPDFMode() && <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />}
    </QueryClientProvider>,
);
